import Breadcrumb from "@/components/Breadcrumb";
import BackButton from "@/components/buttons/BackButton";
import ManageProcessModal from "@/components/material-code/ManageProcessModal";
import {
  useCreateMaterialCode,
  useGetFormulars,
  useGetMaterialCodes,
  useGetMaterialCodesTotal,
  useGetMaterialSupplierById,
  useUpdateMaterialCode,
} from "@/hooks/materialSupplierHook";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import type {
  CreateMaterialCodePayload,
  Formular,
  UpdateMaterialCodePayload,
} from "../../api/materialSupplierService";
import { useGetDimensions } from "@/hooks/masterHook";
import MaterialCodeTable from "./MaterialCodeTable";
import { Add } from "@mui/icons-material";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  DefaultPrimaryButton,
  DefaultSecondaryButton,
  NormalSecondaryButton,
} from "@/styles/globalstyles";
import { getAxiosErrorMessage } from "@/utils/healper";
import EditIcon from "@mui/icons-material/Edit";
import { usePermissions } from "@/hooks/usePermissions";
import Swal from "sweetalert2";
import { useToast } from "@/hooks/useToast";
import { ErrorAlert } from "@/utils/alert";

interface DebouncedSearchInputProps {
  initialValue: string;
  isLoading: boolean;
  disabled?: boolean;
  onSearch: (value: string) => void;
}

const DebouncedSearchInput = ({ initialValue, isLoading, disabled, onSearch }: DebouncedSearchInputProps) => {
  const [value, setValue] = useState<string>(initialValue);
  const isPending = value !== initialValue;

  useEffect(() => {
    const timer = setTimeout(() => onSearch(value), 700);
    return () => clearTimeout(timer);
  }, [value, onSearch]);

  return (
    <TextField
      label="ค้นหา Material Code, Type, Spec, 2nd Tier Supplier, Return Scrap Supplier, 1st Tier Supplier, Ref Part No."
      variant="outlined"
      size="small"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      disabled={disabled}
      slotProps={{
        input: {
          endAdornment: (isLoading || isPending) && (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          ),
        },
      }}
      sx={{
        mt: 2,
        mb: 1,
        width: "70%",
        "& .MuiInputLabel-root": {
          fontSize: "0.8rem",
          color: "#666666",
          "&.Mui-focused": {
            color: "primary.main",
          },
        },
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: "#cccccc",
          },
          "&:hover fieldset": {
            borderColor: "#999999",
          },
        },
      }}
    />
  );
};

const MaterialCodeManagement = () => {
  const canEdit = usePermissions().canEdit("material.supplier");
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { materialSupplierId } = useParams<{ materialSupplierId: string }>();

  const tabActive = useMemo(() => Number(searchParams.get("active")) || 0, [searchParams]);
  const tabDimension = useMemo(() => Number(searchParams.get("dimension")) || 0, [searchParams]);
  const page = useMemo(() => Number(searchParams.get("page")) || 0, [searchParams]);
  const rowsPerPage = useMemo(() => Number(searchParams.get("limit")) || 25, [searchParams]);
  const searchFilter = useMemo(() => searchParams.get("search") || "", [searchParams]);

  const handleSearch = useCallback((value: string) => {
    setSearchParams(
      (prev) => {
        if ((prev.get("search") || "") === value) return prev;
        const newParams = new URLSearchParams(prev);
        if (value) {
          newParams.set("search", value);
        } else {
          newParams.delete("search");
        }
        newParams.set("page", "0");
        return newParams;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isManageProcessModalOpen, setIsManageProcessModalOpen] =
    useState<boolean>(false);
  const [newMaterial, setNewMaterial] = useState<CreateMaterialCodePayload>(
    {} as CreateMaterialCodePayload,
  );
  const [selectedMaterial, setSelectedMaterial] = useState<
    Array<CreateMaterialCodePayload>
  >([]);
  const [isOpenDetail, setIsOpenDetail] = useState<boolean>(false);
  const [isOpenDimension, setIsOpenDimension] = useState<boolean>(false);
  const [isOpenComponentPrice, setIsOpenComponentPrice] =
    useState<boolean>(false);
  const [isOpenOtherCost, setIsOpenOtherCost] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<
    Map<number, Set<string>>
  >(new Map());

  const {
    data: materialSupplier,
    isLoading: materialSupplierIsLoading,
    isError: materialSupplierIsError,
  } = useGetMaterialSupplierById(
    materialSupplierId ? parseInt(materialSupplierId) : 0,
  );

  const {
    data: formularData,
    isLoading: formularIsLoading,
    isError: formularIsError,
  } = useGetFormulars();

  const {
    data: dimensionsData,
    isLoading: dimensionsIsLoading,
    isError: dimensionsIsError,
  } = useGetDimensions();

  const {
    data: materialCodesData,
    isLoading: materialCodesIsLoading,
    isError: materialCodesIsError,
  } = useGetMaterialCodes({
    skip: page * rowsPerPage,
    limit: rowsPerPage,
    material_supplier_id: materialSupplierId ? parseInt(materialSupplierId) : 0,
    material_dimension_id: tabDimension || undefined,
    is_active: tabActive === 0,
    search: searchFilter || undefined,
  });

  const {
    data: materialCodesTotal,
    isLoading: materialCodesTotalIsLoading,
    isError: materialCodesTotalIsError,
  } = useGetMaterialCodesTotal({
    material_supplier_id: materialSupplierId ? parseInt(materialSupplierId) : 0,
    is_active: tabActive === 0,
  });

  const { mutate: createMaterialCode, isPending: createMaterialCodeIsLoading } =
    useCreateMaterialCode();

  const { mutate: updateMaterialCode, isPending: updateMaterialCodeIsLoading } =
    useUpdateMaterialCode();

  const haveOtherCostColumn = useMemo((): boolean => {
    return (
      materialCodesData?.dimensions?.some((material) =>
        material.formula_columns.some(
          (col) => col.parameter?.toLocaleLowerCase() === "others_cost",
        ),
      ) ?? false
    );
  }, [materialCodesData]);

  const formula = useMemo((): Formular | undefined => {
    return formularData?.find(
      (formula) => formula.formula_id === materialSupplier?.formula?.formula_id,
    );
  }, [formularData, materialSupplier]);

  const dimensionTabs = useMemo(() => {
    const tabs = dimensionsData
      ?.map((dimension) => ({
        ...dimension,
        itemCount:
          materialCodesTotal?.find((t) => t.material_dimension_id === dimension.id)
            ?.total || 0,
      }))
      .sort((a, b) => {
        if (b.itemCount !== a.itemCount) return b.itemCount - a.itemCount;
        return a.id - b.id;
      });

    if (tabs && tabs.length > 0 && materialCodesTotal && !isAdding) {
      const found = tabs.some((d) => d.id === tabDimension);
      if (!found || searchParams.get("dimension") === null) {
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("dimension", String(tabs[0].id));
            return newParams;
          },
          { replace: true },
        );
      }
    }

    return tabs;
  }, [
    dimensionsData,
    materialCodesTotal,
    isAdding,
    tabDimension,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    document.title = "Material Supplier | AMPA";
  }, []);

  useQueryHandler({
    isError:
      materialSupplierIsError ||
      dimensionsIsError ||
      formularIsError ||
      materialCodesIsError ||
      materialCodesTotalIsError,
    isFetching:
      createMaterialCodeIsLoading ||
      updateMaterialCodeIsLoading ||
      materialSupplierIsLoading ||
      dimensionsIsLoading ||
      formularIsLoading ||
      materialCodesTotalIsLoading,
  });

  const validateMaterialCode = (
    material: CreateMaterialCodePayload,
  ): Set<string> => {
    const errors = new Set<string>();

    if (!material.material_type_id) {
      errors.add("material_type_id");
    }
    if (!material.material_spec_id) {
      errors.add("material_spec_id");
    }
    if (!material.first_tier_supplier_id) {
      errors.add("first_tier_supplier_id");
    }

    return errors;
  };

  const validateSelectedMaterials = (
    materials: Array<CreateMaterialCodePayload>,
  ): boolean => {
    const errorsMap = new Map<number, Set<string>>();
    let hasErrors = false;

    materials.forEach((material) => {
      const materialId = material.material_id || 0;
      const errors = validateMaterialCode(material);
      if (errors.size > 0) {
        errorsMap.set(materialId, errors);
        hasErrors = true;
      }
    });

    setValidationErrors(errorsMap);
    return !hasErrors;
  };

  const handleChangeTabActive = (
    _event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setSelectedMaterial([]);
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("active", String(newValue));
        newParams.set("page", "0");
        return newParams;
      },
      { replace: true },
    );
  };

  const handleChangeTabDimension = (
    _event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setSelectedMaterial([]);
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("dimension", String(newValue));
        newParams.set("page", "0");
        return newParams;
      },
      { replace: true },
    );
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("page", String(newPage));
        return newParams;
      },
      { replace: true },
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("limit", event.target.value);
        newParams.set("page", "0");
        return newParams;
      },
      { replace: true },
    );
  };

  const handleAddMaterialCode = () => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("active", "0");
        newParams.set("page", "0");
        return newParams;
      },
      { replace: true },
    );
    setIsAdding(true);
    setIsOpenDetail(true);
    setIsOpenDimension(true);
    setIsOpenComponentPrice(true);
    setIsOpenOtherCost(true);
    setSelectedMaterial([]);
  };

  const handleCancelAddMaterialCode = () => {
    setIsAdding(false);
    setNewMaterial({} as CreateMaterialCodePayload);
    setValidationErrors(new Map());
  };

  const handleDuplicateMaterialCode = () => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("active", "0");
        newParams.set("page", "0");
        return newParams;
      },
      { replace: true },
    );
    setIsAdding(true);
    setIsOpenDetail(true);
    setIsOpenDimension(true);
    setIsOpenComponentPrice(true);
    setIsOpenOtherCost(true);
    setNewMaterial(
      selectedMaterial && selectedMaterial.length === 1
        ? {
            ...selectedMaterial[0],
            material_code: "",
          }
        : ({} as CreateMaterialCodePayload),
    );
  };

  const handleSaveAddMaterialCode = () => {
    const matCodeDataByDimension = materialCodesData?.dimensions?.find(
      (dim) => dim.dimension_id === tabDimension,
    );
    if (!matCodeDataByDimension) {
      ErrorAlert("Error", "ข้อมูล dimension ยังโหลดไม่เสร็จ กรุณารอสักครู่แล้วลองใหม่");
      return;
    }
    if (newMaterial) {
      const dimensionParams: Record<string, number> = Object.fromEntries(
        matCodeDataByDimension?.dimension_columns.map((col) => [
          col.parameter,
          newMaterial.dimension_parameters
            ? newMaterial.dimension_parameters[col.parameter] || 0
            : 0,
        ]) || [],
      );
      const formulaParams: Record<string, number | boolean> =
        Object.fromEntries(
          matCodeDataByDimension?.formula_columns.map((col) => [
            col.parameter,
            newMaterial.formula_parameters
              ? newMaterial.formula_parameters[col.parameter] || 0
              : 0,
          ]) || [],
        );

      const lossType = matCodeDataByDimension?.loss_type?.trim().toLowerCase();
      if (formulaParams.loss1 !== undefined) {
        formulaParams.is_loss1_pct =
          newMaterial.formula_parameters?.is_loss1_pct ?? false;
        if (lossType === "thb") {
          formulaParams.is_loss1_pct = false;
        }
        if (lossType === "pct") {
          formulaParams.is_loss1_pct = true;
        }
      }
      if (formulaParams.loss2 !== undefined) {
        formulaParams.is_loss2_pct =
          newMaterial.formula_parameters?.is_loss2_pct ?? false;
        if (lossType === "thb") {
          formulaParams.is_loss2_pct = false;
        }
        if (lossType === "pct") {
          formulaParams.is_loss2_pct = true;
        }
      }

      const formData: CreateMaterialCodePayload = {
        ...newMaterial,
        material_supplier_id: materialSupplierId
          ? parseInt(materialSupplierId)
          : 0,
        formula_id: materialSupplier?.formula?.formula_id || 0,
        material_dimension_id: dimensionsData
          ? dimensionsData?.find((dim) => dim.id === tabDimension)?.id || 0
          : 0,
        is_active: true,
        dimension_parameters: dimensionParams,
        formula_parameters: formulaParams,
      };
      if (!formData.return_scrap_supplier_id) {
        delete formData.return_scrap_supplier_id;
      }
      if (!formData.second_tier_supplier_id) {
        delete formData.second_tier_supplier_id;
      }

      const errors = validateMaterialCode(formData);
      if (errors.size > 0) {
        const errorsMap = new Map<number, Set<string>>();
        errorsMap.set(0, errors);
        setValidationErrors(errorsMap);
        ErrorAlert("Error", "กรุณากรอกข้อมูลให้ครบถ้วน");
        return;
      }

      createMaterialCode(formData, {
        onSuccess: () => {
          setNewMaterial({} as CreateMaterialCodePayload);
          setIsAdding(false);
          setValidationErrors(new Map());
          showToast("เพิ่ม Material Code สำเร็จ", "success");
        },
        onError: (error) => {
          ErrorAlert("Error", getAxiosErrorMessage(error));
          console.log("Create Material Code Error:", error);
        },
      });
    }
  };

  const handleEditMaterialCode = () => {
    setIsEditing(true);
    setIsOpenDetail(true);
    setIsOpenDimension(true);
    setIsOpenComponentPrice(true);
    setIsOpenOtherCost(true);
  };

  const handleActivateMaterialCode = () => {
    Swal.fire({
      title: "Activate",
      text: "You are about to activate the selected material codes.",
      icon: "warning",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        if (selectedMaterial && selectedMaterial.length > 0) {
          const updatedMaterials: UpdateMaterialCodePayload[] =
            selectedMaterial.map((material) => ({
              material_id: material.material_id!,
              is_active: true,
            }));
          updateMaterialCode(updatedMaterials, {
            onSuccess: () => {
              showToast("Activate Material Code สำเร็จ", "success");
            },
            onError: (error) => {
              ErrorAlert("Error", getAxiosErrorMessage(error));
              console.log("Activate Material Code Error:", error);
            },
          });
          setIsEditing(false);
          setSelectedMaterial([]);
        }
      }
    });
  };

  const handleDeactivateMaterialCode = () => {
    Swal.fire({
      title: "Deactivate",
      text: "You are about to deactivate the selected material codes.",
      icon: "warning",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        if (selectedMaterial && selectedMaterial.length > 0) {
          const updatedMaterials: UpdateMaterialCodePayload[] =
            selectedMaterial.map((material) => ({
              material_id: material.material_id!,
              is_active: false,
            }));
          updateMaterialCode(updatedMaterials, {
            onSuccess: () => {
              showToast("Deactivate Material Code สำเร็จ", "success");
            },
            onError: (error) => {
              ErrorAlert("Error", getAxiosErrorMessage(error));
              console.log("Deactivate Material Code Error:", error);
            },
          });
          setIsEditing(false);
          setSelectedMaterial([]);
        }
      }
    });
  };

  const handleCancelEditMaterialCode = () => {
    setIsEditing(false);
    setSelectedMaterial([]);
    setValidationErrors(new Map());
  };

  const handleManageProcess = () => {
    setIsManageProcessModalOpen(true);
  };

  const handleSaveEditMaterialCode = () => {
    if (selectedMaterial && selectedMaterial.length > 0) {
      if (!validateSelectedMaterials(selectedMaterial)) {
        ErrorAlert("Error", "กรุณากรอกข้อมูลให้ครบถ้วน");
        return;
      }

      const updatedMaterials: UpdateMaterialCodePayload[] =
        selectedMaterial.map((material) => {
          const updated = {
            ...material,
            material_id: material.material_id!,
            material_supplier_id: materialSupplierId
              ? parseInt(materialSupplierId)
              : 0,
          };
          if (!updated.return_scrap_supplier_id) {
            delete updated.return_scrap_supplier_id;
          }
          if (!updated.second_tier_supplier_id) {
            delete updated.second_tier_supplier_id;
          }
          return updated;
        });
      updateMaterialCode(updatedMaterials, {
        onSuccess: () => {
          setValidationErrors(new Map());
          showToast("แก้ไข Material Code สำเร็จ", "success");
        },
        onError: (error) => {
          ErrorAlert("Error", getAxiosErrorMessage(error));
          console.log("Edit Material Code Error:", error);
        },
      });
      setIsEditing(false);
      setSelectedMaterial([]);
    }
  };

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb
        current={materialSupplier?.name_abbr}
        previous={[{ key: "material.supplier" }]}
      />
      <Box mt={1}>
        <Box
          display={"flex"}
          alignItems="center"
          justifyContent="flex-start"
          gap={2}
        >
          <BackButton to="/material-supplier" />
          <Typography variant="body1" fontWeight={600}>
            {materialSupplier?.name}
          </Typography>
          <Tooltip title={materialSupplier?.address_text || ""}>
            <Typography
              variant="body1"
              color="text.secondary"
              maxWidth={500}
              noWrap
            >
              Address: {materialSupplier?.address_text}
            </Typography>
          </Tooltip>
          <Tooltip title={materialSupplier?.phones.join(", ") || ""}>
            <Typography
              variant="body1"
              color="text.secondary"
              maxWidth={500}
              noWrap
            >
              Tel: {materialSupplier?.phones.join(", ")}
            </Typography>
          </Tooltip>
        </Box>
        <Box mt={1} display={"flex"} gap={1}>
          {formula && formula.has_commission_pct && (
            <Chip
              size="medium"
              sx={{ height: 40, fontSize: 14, fontWeight: 500 }}
              label={`Commission :  ${
                materialSupplier?.attributes?.commission_pct || 0
              }%`}
            />
          )}
          {formula && formula.has_stock_pct && (
            <Chip
              size="medium"
              sx={{ height: 40, fontSize: 14, fontWeight: 500 }}
              label={`Stock :  ${
                materialSupplier?.attributes?.stock_pct || 0
              }%`}
            />
          )}
          {formula && formula.has_credit_pct && (
            <Chip
              size="medium"
              sx={{ height: 40, fontSize: 14, fontWeight: 500 }}
              label={`Credit :  ${
                materialSupplier?.attributes?.credit_pct || 0
              }%`}
            />
          )}
          {formula && formula.has_rounding && formula.has_rounding_digits && (
            <Chip
              size="medium"
              sx={{ height: 40, fontSize: 14, fontWeight: 500 }}
              label={`Rouning :  ${
                materialSupplier?.attributes?.rounding || "ROUND"
              } ${materialSupplier?.attributes?.rounding_digits || 0} Digits`}
            />
          )}
          {formula && formula.has_usance && (
            <Chip
              size="medium"
              sx={{ height: 40, fontSize: 14, fontWeight: 500 }}
              label={`Usance :  ${
                materialSupplier?.attributes?.usance || 0
              } Days`}
            />
          )}
          {formula && formula.has_interest && (
            <Chip
              size="medium"
              sx={{ height: 40, fontSize: 14, fontWeight: 500 }}
              label={`Interest :  ${
                materialSupplier?.attributes?.interest || 0
              } %`}
            />
          )}
        </Box>
        <Box mt={1}>
          <Tabs
            value={tabActive}
            onChange={handleChangeTabActive}
            aria-label="tabs-active"
          >
            <Tab key={0} label={"Active"} disabled={isAdding || isEditing} />
            <Tab key={1} label={"Inactive"} disabled={isAdding || isEditing} />
          </Tabs>
        </Box>
        <Box>
          <Tabs
            value={tabDimension}
            onChange={handleChangeTabDimension}
            aria-label="tabs-dimension"
          >
            {dimensionTabs?.map((dimension) => (
              <Tab
                key={dimension.id}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {dimension.dimension_name}
                    <Chip
                      label={dimension.itemCount}
                      size="small"
                      sx={{
                        backgroundColor:
                          dimension.itemCount > 0 ? "#f44336" : "#9e9e9e",
                        color: "white",
                        fontWeight: 600,
                        height: 20,
                        fontSize: 11,
                        "& .MuiChip-label": {
                          px: 1,
                        },
                      }}
                    />
                  </Box>
                }
                disabled={isAdding || isEditing}
                value={dimension?.id}
              />
            ))}
          </Tabs>
          <Box mt={2} display={"flex"} justifyContent="space-between">
            <Box>
              {!isAdding && !isEditing && haveOtherCostColumn && (
                <>
                  <Button
                    variant="outlined"
                    sx={{
                      ...DefaultSecondaryButton,
                      color: "#f57c00",
                      borderColor: "#f57c00",
                      fontSize: 12,
                      width: 140,
                    }}
                    onClick={handleManageProcess}
                    disabled={!canEdit}
                  >
                    จัดการ Process
                  </Button>
                </>
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="body2"
                color="textSecondary"
                fontSize="0.9rem"
                mr={1}
              >
                Selected {selectedMaterial?.length || 0} items
              </Typography>
              {!isAdding && !isEditing && (
                <>
                  <Button
                    variant="outlined"
                    sx={{ ...DefaultSecondaryButton, fontSize: 12, width: 120 }}
                    onClick={handleDuplicateMaterialCode}
                    disabled={selectedMaterial?.length !== 1 || !canEdit}
                  >
                    <ContentCopyIcon /> Duplicate
                  </Button>
                  {tabActive === 0 ? (
                    <Button
                      variant="outlined"
                      sx={{
                        ...DefaultSecondaryButton,
                        fontSize: 12,
                        width: 120,
                        color: "#d32f2f",
                        borderColor: "#d32f2f",
                      }}
                      onClick={handleDeactivateMaterialCode}
                      disabled={selectedMaterial?.length === 0 || !canEdit}
                    >
                      <DoNotDisturbIcon /> Deactivate
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      sx={{
                        ...DefaultSecondaryButton,
                        fontSize: 12,
                        width: 120,
                        color: "success.light",
                        borderColor: "success.light",
                      }}
                      onClick={handleActivateMaterialCode}
                      disabled={selectedMaterial?.length === 0 || !canEdit}
                    >
                      <CheckIcon /> Activate
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    sx={{ ...DefaultSecondaryButton, fontSize: 12, width: 120 }}
                    onClick={handleEditMaterialCode}
                    disabled={selectedMaterial?.length === 0 || !canEdit}
                  >
                    <EditIcon /> แก้ไข
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{ ...DefaultSecondaryButton, fontSize: 12, width: 120 }}
                    onClick={handleAddMaterialCode}
                    disabled={!canEdit}
                  >
                    <Add /> เพิ่ม
                  </Button>
                </>
              )}
              {(isAdding || isEditing) && (
                <>
                  <Button
                    variant="outlined"
                    sx={{ ...NormalSecondaryButton, fontSize: 12 }}
                    onClick={
                      isAdding
                        ? handleCancelAddMaterialCode
                        : handleCancelEditMaterialCode
                    }
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{ ...DefaultPrimaryButton, fontSize: 12 }}
                    onClick={
                      isAdding
                        ? handleSaveAddMaterialCode
                        : handleSaveEditMaterialCode
                    }
                  >
                    บันทึก
                  </Button>{" "}
                </>
              )}
            </Box>
          </Box>
          <Box display={"flex"} justifyContent="space-between" alignItems="center">
            <Box ml={2}>
              <FormControlLabel
                label="Detail"
                control={
                  <Checkbox
                    checked={isOpenDetail}
                    onChange={() => setIsOpenDetail(!isOpenDetail)}
                  />
                }
              />
              <FormControlLabel
                label="Dimension"
                control={
                  <Checkbox
                    checked={isOpenDimension}
                    onChange={() => setIsOpenDimension(!isOpenDimension)}
                  />
                }
              />
              <FormControlLabel
                label="Component Price"
                control={
                  <Checkbox
                    checked={isOpenComponentPrice}
                    onChange={() =>
                      setIsOpenComponentPrice(!isOpenComponentPrice)
                    }
                  />
                }
              />
              <FormControlLabel
                label="Other Costs"
                control={
                  <Checkbox
                    checked={isOpenOtherCost}
                    onChange={() => setIsOpenOtherCost(!isOpenOtherCost)}
                  />
                }
              />
            </Box>
            <Box ml={2} flexGrow={1} display="flex" justifyContent="flex-end">
              <DebouncedSearchInput
                initialValue={searchFilter}
                isLoading={materialCodesIsLoading}
                disabled={isAdding || isEditing}
                onSearch={handleSearch}
              />
            </Box>
          </Box>
          <MaterialCodeTable
            materials={materialCodesData?.dimensions || []}
            dimensionId={tabDimension ?? 0}
            dimensionName={
              dimensionsData?.find((dim) => dim.id === tabDimension)
                ?.dimension_name || ""
            }
            loading={materialCodesIsLoading || createMaterialCodeIsLoading}
            isAdding={isAdding}
            isEditing={isEditing}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={materialCodesData?.total || 0}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            newMaterial={newMaterial}
            setNewMaterial={setNewMaterial}
            selectedMaterial={selectedMaterial || []}
            setSelectedMaterial={setSelectedMaterial}
            materialSupplierDetail={materialSupplier}
            isOpenDetail={isOpenDetail}
            isOpenDimension={isOpenDimension}
            isOpenComponentPrice={isOpenComponentPrice}
            isOpenOtherCost={isOpenOtherCost}
            validationErrors={validationErrors}
            tabActive={tabActive}
          />
        </Box>
      </Box>

      <ManageProcessModal
        open={isManageProcessModalOpen}
        handleClose={() => setIsManageProcessModalOpen(false)}
        materialId={materialSupplierId ? parseInt(materialSupplierId) : 0}
      />
    </Box>
  );
};

export default MaterialCodeManagement;
